import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Badge, Spinner, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder, faFolderOpen, faFile, faHdd, faPrint, faPlug, faEyeSlash,
  faDownload, faSync, faSearch, faNetworkWired, faChevronRight, faChevronDown,
  faLock, faLockOpen, faExclamationTriangle, faServer, faProjectDiagram,
  faList, faSortUp, faSortDown, faSort, faArrowLeft, faFileExport, faFileCsv,
  faStop, faClock, faSkullCrossbones, faKey, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import * as d3 from 'd3';
import c2Service from '../../services/c2Service';
import { toast } from 'react-toastify';

const ShareBrowser = ({ beacons, projectId }) => {
  // State — preferences restored from localStorage
  const [selectedBeacon, setSelectedBeacon] = useState(() => localStorage.getItem('fb_beacon') || '');
  const [target, setTarget] = useState(() => localStorage.getItem('fb_target') || '');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // ShareBrowseResult
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedPath, setSelectedPath] = useState(null);
  const [browsedContents, setBrowsedContents] = useState({}); // uncPath → entries
  const [loadingPaths, setLoadingPaths] = useState(new Set());
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('fb_viewMode') || 'explorer');
  const [browseMode, setBrowseMode] = useState(() => localStorage.getItem('fb_browseMode') || 'shares');
  const [localPath, setLocalPath] = useState(() => localStorage.getItem('fb_localPath') || 'C:\\');
  const [localRoots, setLocalRoots] = useState(null); // [{name, isDir, ...}] for local root listing
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [scanHistory, setScanHistory] = useState([]); // list of past targets

  // Credentials (optional — defaults to agent's logged-in user)
  const [showCreds, setShowCreds] = useState(false);
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [credDomain, setCredDomain] = useState('');

  // Scan progress tracking
  const [scanStartTime, setScanStartTime] = useState(null);
  const [scanElapsed, setScanElapsed] = useState(0);
  const [scanStatus, setScanStatus] = useState(''); // 'creating' | 'waiting' | 'sent' | 'agent_offline'
  const activeTaskIdRef = useRef(null);
  const abortRef = useRef(false);

  // Refs
  const svgRef = useRef(null);
  const graphContainerRef = useRef(null);

  // === Cache management — persist browsed data across tab switches ===
  const stateRef = useRef();
  const prevBeaconRef = useRef(undefined); // undefined = first mount

  // Keep ref in sync with latest state (runs after every render)
  useEffect(() => {
    stateRef.current = {
      scanResult, browsedContents, localRoots,
      expandedNodes: [...expandedNodes],
      selectedPath, breadcrumb, browseMode, target, localPath
    };
  });

  // Persist preferences to localStorage
  useEffect(() => { if (selectedBeacon) localStorage.setItem('fb_beacon', selectedBeacon); }, [selectedBeacon]);
  useEffect(() => { localStorage.setItem('fb_viewMode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('fb_browseMode', browseMode); }, [browseMode]);
  useEffect(() => { localStorage.setItem('fb_localPath', localPath); }, [localPath]);
  useEffect(() => { if (target !== undefined) localStorage.setItem('fb_target', target); }, [target]);

  // Restore cached browse data on mount
  useEffect(() => {
    const beaconId = localStorage.getItem('fb_beacon');
    if (beaconId) {
      try {
        const cached = localStorage.getItem('fb_cache_' + beaconId);
        if (cached) {
          const c = JSON.parse(cached);
          if (c.scanResult) setScanResult(c.scanResult);
          if (c.browsedContents && Object.keys(c.browsedContents).length) setBrowsedContents(c.browsedContents);
          if (c.localRoots) setLocalRoots(c.localRoots);
          if (c.expandedNodes?.length) setExpandedNodes(new Set(c.expandedNodes));
          if (c.selectedPath) setSelectedPath(c.selectedPath);
          if (c.breadcrumb?.length) setBreadcrumb(c.breadcrumb);
        }
      } catch (e) { /* ignore */ }
    }
    prevBeaconRef.current = beaconId || '';
  }, []);

  // Save cache to localStorage on unmount + periodically (evidence persists across browser sessions)
  useEffect(() => {
    const saveCache = () => {
      const beaconId = localStorage.getItem('fb_beacon');
      if (beaconId && stateRef.current) {
        try { localStorage.setItem('fb_cache_' + beaconId, JSON.stringify(stateRef.current)); } catch (e) {}
      }
    };
    // Auto-save every 10 seconds while mounted
    const interval = setInterval(saveCache, 10000);
    return () => {
      clearInterval(interval);
      saveCache(); // Final save on unmount
    };
  }, []);

  // On beacon switch: save old cache, restore new cache
  useEffect(() => {
    if (prevBeaconRef.current === undefined) return; // skip initial mount
    if (prevBeaconRef.current === selectedBeacon) return;

    // Save previous beacon's state
    if (prevBeaconRef.current && stateRef.current) {
      try { localStorage.setItem('fb_cache_' + prevBeaconRef.current, JSON.stringify(stateRef.current)); } catch (e) {}
    }

    // Restore new beacon's cache or reset
    let restored = false;
    if (selectedBeacon) {
      try {
        const cached = localStorage.getItem('fb_cache_' + selectedBeacon);
        if (cached) {
          const c = JSON.parse(cached);
          setScanResult(c.scanResult || null);
          setBrowsedContents(c.browsedContents || {});
          setLocalRoots(c.localRoots || null);
          setExpandedNodes(new Set(c.expandedNodes || []));
          setSelectedPath(c.selectedPath || null);
          setBreadcrumb(c.breadcrumb || []);
          restored = true;
        }
      } catch (e) {}
    }
    if (!restored) {
      setScanResult(null); setBrowsedContents({}); setLocalRoots(null);
      setExpandedNodes(new Set()); setSelectedPath(null); setBreadcrumb([]);
    }

    prevBeaconRef.current = selectedBeacon;
  }, [selectedBeacon]);

  // Validate restored beacon exists in current project
  useEffect(() => {
    if (selectedBeacon && beacons.length > 0 && !beacons.find(b => b.beaconId === selectedBeacon)) {
      setSelectedBeacon('');
    }
  }, [beacons]);

  // === Background task tracking — pick up results even when away ===
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // Process completed browse_path result
  const applyLocalBrowseResult = useCallback((output, path) => {
    let entries;
    try { entries = JSON.parse(output); } catch { toast.error('Failed to parse browse result'); return; }
    const cleanPath = path.replace(/\\$/, '');
    setLocalRoots(entries);
    setBrowsedContents(prev => ({ ...prev, [cleanPath]: entries }));
    setSelectedPath(cleanPath);
    setBreadcrumb(buildBreadcrumb(cleanPath));
    toast.success(`Listed ${entries.length} items in ${path}`);
  }, []);

  // Process completed enum_shares result
  const applyScanResult = useCallback((output, targetStr) => {
    let parsed;
    try { parsed = JSON.parse(output); } catch { toast.error('Failed to parse share scan result'); return; }
    if (parsed.error) toast.error(`Share scan error: ${parsed.error}`);
    setScanResult(parsed);
    if (parsed.contents) {
      const freshContents = {};
      for (const [key, entries] of Object.entries(parsed.contents)) {
        if (key.startsWith('\\\\')) {
          freshContents[key] = entries;
        } else {
          const share = parsed.shares?.find(s => s.name === key);
          if (share) freshContents[share.uncPath] = entries;
        }
      }
      // Merge: new scan results overlay existing, but previously browsed subdirs are kept
      setBrowsedContents(prev => ({ ...prev, ...freshContents }));
    }
    const hostsFound = parsed.withShares || (parsed.shares?.length > 0 ? 1 : 0);
    const shareCount = parsed.shares?.length || 0;
    if (parsed.scanned > 1) {
      toast.success(`Scanned ${parsed.scanned} hosts — found ${shareCount} shares on ${hostsFound} host(s)`);
    } else {
      toast.success(`Found ${shareCount} shares on ${targetStr}`);
    }
  }, []);

  // On mount: check for pending tasks that may have completed while away
  useEffect(() => {
    const checkPendingTask = async () => {
      const pendingStr = sessionStorage.getItem('fb_pendingTask');
      if (!pendingStr) return;
      const pending = JSON.parse(pendingStr);
      const beaconId = localStorage.getItem('fb_beacon');
      if (pending.beaconId !== beaconId) return;

      try {
        const res = await c2Service.getTask(pending.taskId);
        const task = res.data?.data || res.data;
        if (!mountedRef.current) return;

        if (task.status === 'completed') {
          sessionStorage.removeItem('fb_pendingTask');
          const output = task.result?.output || task.output;
          if (pending.type === 'browse_path') {
            applyLocalBrowseResult(output, pending.path);
          } else {
            applyScanResult(output, pending.path);
          }
        } else if (task.status === 'failed' || task.status === 'cancelled') {
          sessionStorage.removeItem('fb_pendingTask');
          toast.error(`Task ${task.status}: ${task.result?.error || 'unknown error'}`);
        } else {
          // Still running — resume polling
          setScanning(true);
          try {
            const completed = await pollTask(pending.taskId);
            if (!mountedRef.current) return;
            sessionStorage.removeItem('fb_pendingTask');
            const output = completed.result?.output || completed.output;
            if (pending.type === 'browse_path') {
              applyLocalBrowseResult(output, pending.path);
            } else {
              applyScanResult(output, pending.path);
            }
          } catch (err) {
            if (mountedRef.current) toast.error(`Task failed: ${err.message}`);
            sessionStorage.removeItem('fb_pendingTask');
          } finally {
            if (mountedRef.current) setScanning(false);
          }
        }
      } catch (e) {
        sessionStorage.removeItem('fb_pendingTask');
      }
    };
    checkPendingTask();
  }, []);

  // Elapsed time counter while scanning
  useEffect(() => {
    if (!scanning || !scanStartTime) return;
    const interval = setInterval(() => {
      setScanElapsed(Math.floor((Date.now() - scanStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [scanning, scanStartTime]);

  // Estimate number of hosts in target range
  const estimateHostCount = (targetStr) => {
    if (!targetStr) return 1;
    const t = targetStr.trim();
    // CIDR: 192.168.1.0/24 → 254 hosts
    const cidrMatch = t.match(/\/(\d+)$/);
    if (cidrMatch) {
      const prefix = parseInt(cidrMatch[1]);
      return Math.max(1, Math.pow(2, 32 - prefix) - 2);
    }
    // Range: 192.168.1.1-255 → 255 hosts
    const rangeMatch = t.match(/(\d+)-(\d+)$/);
    if (rangeMatch) {
      return Math.max(1, parseInt(rangeMatch[2]) - parseInt(rangeMatch[1]) + 1);
    }
    // Comma-separated
    if (t.includes(',')) return t.split(',').length;
    // Single host
    return 1;
  };

  // Format elapsed time
  const formatElapsed = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  // Stop/cancel active scan
  const handleStop = async () => {
    abortRef.current = true;
    const taskId = activeTaskIdRef.current;
    if (taskId) {
      try {
        await c2Service.cancelTask(taskId);
        toast.info('Scan cancelled');
      } catch (err) {
        // Task may have already completed or failed
        console.warn('Cancel failed (task may have already finished):', err.message);
      }
    }
    activeTaskIdRef.current = null;
    sessionStorage.removeItem('fb_pendingTask');
    setScanning(false);
    setScanStartTime(null);
    setScanElapsed(0);
    setScanStatus('');
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes === 0) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('sv-SE');
    } catch {
      return dateStr;
    }
  };

  // Poll task until completion (supports abort + status updates)
  const pollTask = async (taskId, beaconId) => {
    const maxAttempts = 300; // 10 minutes max (2s intervals)
    let lastStatus = '';
    let offlineChecks = 0;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));

      // Check for abort
      if (abortRef.current) {
        throw new Error('Scan cancelled');
      }

      try {
        const res = await c2Service.getTask(taskId);
        const task = res.data?.data || res.data;

        // Update status for progress indicator
        if (task.status !== lastStatus) {
          lastStatus = task.status;
          if (mountedRef.current) setScanStatus(task.status);
        }

        if (task.status === 'completed') {
          return task;
        }
        if (task.status === 'failed' || task.status === 'cancelled') {
          throw new Error(task.result?.error || `Task ${task.status}`);
        }

        // Check if task is stuck in 'sent' and beacon went offline
        if (task.status === 'sent' && beaconId && i > 5 && i % 5 === 0) {
          const beacon = beacons.find(b => b.beaconId === beaconId);
          if (beacon && !beacon.isAlive) {
            offlineChecks++;
            if (mountedRef.current) setScanStatus('agent_offline');
            // After 3 consecutive offline checks (30s), the agent is likely dead
            if (offlineChecks >= 3) {
              throw new Error('Agent appears offline — it may have crashed or been killed (e.g., by Defender). Check agent status.');
            }
          } else {
            offlineChecks = 0;
          }
        }
      } catch (err) {
        if (err.message.includes('Task') || err.message.includes('cancelled') || err.message.includes('Agent appears')) throw err;
        // Network error — retry
      }
    }
    throw new Error('Task timed out after 10 minutes');
  };

  // Scan shares on target
  const handleScan = async () => {
    if (!selectedBeacon || !target) {
      toast.warning('Select a beacon and enter a target hostname/IP');
      return;
    }

    const beacon = beacons.find(b => b.beaconId === selectedBeacon);
    if (!beacon) return;

    abortRef.current = false;
    activeTaskIdRef.current = null;
    setScanning(true);
    setScanStartTime(Date.now());
    setScanElapsed(0);
    setScanStatus('creating');
    setScanResult(null);
    // Keep browsedContents — previously browsed subdirectories persist across rescans
    // New scan results will merge via applyScanResult
    setSelectedPath(null);
    setBreadcrumb([]);

    try {
      const args = { target: target.trim(), depth: 1 };
      // Attach credentials if provided
      if (showCreds && credUsername) {
        args.credentials = [{ username: credUsername, password: credPassword, domain: credDomain }];
      }
      const taskRes = await c2Service.createTask(beacon._id, 'enum_shares', args);
      const taskId = taskRes.data?.data?._id || taskRes.data?._id;
      activeTaskIdRef.current = taskId;
      if (mountedRef.current) setScanStatus('waiting');

      // Track pending task so results can be picked up if user navigates away
      sessionStorage.setItem('fb_pendingTask', JSON.stringify({
        taskId, beaconId: selectedBeacon, type: 'enum_shares', path: target.trim()
      }));

      const completed = await pollTask(taskId, selectedBeacon);
      if (!mountedRef.current) return;
      sessionStorage.removeItem('fb_pendingTask');
      activeTaskIdRef.current = null;
      const output = completed.result?.output || completed.output;
      applyScanResult(output, target);

      // Add to history
      setScanHistory(prev => {
        const filtered = prev.filter(h => h !== target.trim());
        return [target.trim(), ...filtered].slice(0, 10);
      });
    } catch (err) {
      if (mountedRef.current && !abortRef.current) toast.error(`Share scan failed: ${err.message}`);
      sessionStorage.removeItem('fb_pendingTask');
      activeTaskIdRef.current = null;
    } finally {
      if (mountedRef.current) {
        setScanning(false);
        setScanStartTime(null);
        setScanElapsed(0);
        setScanStatus('');
      }
    }
  };

  // Browse local filesystem starting path
  const handleLocalBrowse = async () => {
    if (!selectedBeacon || !localPath) {
      toast.warning('Select a beacon and enter a path');
      return;
    }

    const beacon = beacons.find(b => b.beaconId === selectedBeacon);
    if (!beacon) return;

    abortRef.current = false;
    activeTaskIdRef.current = null;
    setScanning(true);
    setScanStartTime(Date.now());
    setScanElapsed(0);
    setScanStatus('creating');
    setExpandedNodes(new Set());
    setSelectedPath(null);
    setBreadcrumb([]);

    try {
      const taskRes = await c2Service.createTask(beacon._id, 'browse_path', {
        path: localPath.trim()
      });
      const taskId = taskRes.data?.data?._id || taskRes.data?._id;
      activeTaskIdRef.current = taskId;
      if (mountedRef.current) setScanStatus('waiting');

      // Track pending task so results can be picked up if user navigates away
      sessionStorage.setItem('fb_pendingTask', JSON.stringify({
        taskId, beaconId: selectedBeacon, type: 'browse_path', path: localPath.trim()
      }));

      const completed = await pollTask(taskId, selectedBeacon);
      if (!mountedRef.current) return;
      sessionStorage.removeItem('fb_pendingTask');
      activeTaskIdRef.current = null;
      const output = completed.result?.output || completed.output;
      applyLocalBrowseResult(output, localPath.trim());
    } catch (err) {
      if (mountedRef.current && !abortRef.current) toast.error(`Browse failed: ${err.message}`);
      sessionStorage.removeItem('fb_pendingTask');
      activeTaskIdRef.current = null;
    } finally {
      if (mountedRef.current) {
        setScanning(false);
        setScanStartTime(null);
        setScanElapsed(0);
        setScanStatus('');
      }
    }
  };

  // Browse a UNC path or local path (lazy load)
  const handleBrowsePath = async (uncPath) => {
    if (browsedContents[uncPath] || loadingPaths.has(uncPath)) return;

    const beacon = beacons.find(b => b.beaconId === selectedBeacon);
    if (!beacon) return;

    setLoadingPaths(prev => new Set(prev).add(uncPath));

    try {
      const taskRes = await c2Service.createTask(beacon._id, 'browse_path', {
        path: uncPath
      });
      const taskId = taskRes.data?.data?._id || taskRes.data?._id;

      const completed = await pollTask(taskId);
      const output = completed.result?.output || completed.output;

      let entries;
      try {
        entries = JSON.parse(output);
      } catch {
        toast.error('Failed to parse browse result');
        return;
      }

      setBrowsedContents(prev => ({ ...prev, [uncPath]: entries }));
    } catch (err) {
      toast.error(`Browse failed: ${err.message}`);
    } finally {
      setLoadingPaths(prev => {
        const next = new Set(prev);
        next.delete(uncPath);
        return next;
      });
    }
  };

  // Download file
  const handleDownload = async (uncPath, fileName) => {
    const beacon = beacons.find(b => b.beaconId === selectedBeacon);
    if (!beacon) return;

    setDownloadingFiles(prev => new Set(prev).add(uncPath));

    try {
      const taskRes = await c2Service.createTask(beacon._id, 'download', {
        remotePath: uncPath
      });
      const taskId = taskRes.data?.data?._id || taskRes.data?._id;
      toast.info(`Downloading ${fileName}...`);

      const completed = await pollTask(taskId);

      // The download task stores file data — check result
      if (completed.files?.length > 0) {
        // File was stored server-side, trigger browser download
        const file = completed.files[0];
        const link = document.createElement('a');
        link.href = `/api/c2/files/${file._id || file.id}/download`;
        link.download = fileName;
        link.click();
        toast.success(`Downloaded ${fileName}`);
      } else {
        toast.info(`File ${fileName} queued for download — check Files tab`);
      }
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    } finally {
      setDownloadingFiles(prev => {
        const next = new Set(prev);
        next.delete(uncPath);
        return next;
      });
    }
  };

  // Toggle tree node
  const toggleNode = (path) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
        // Lazy load if not yet browsed
        if (!browsedContents[path]) {
          handleBrowsePath(path);
        }
      }
      return next;
    });
  };

  // Select a path to show in file list
  const selectPath = (path, segments) => {
    setSelectedPath(path);
    setBreadcrumb(segments);
    if (!browsedContents[path]) {
      handleBrowsePath(path);
    }
  };

  // Get permission color
  const permColor = (perm) => {
    switch (perm) {
      case 'readwrite': return '#48bb78';
      case 'read': return '#ecc94b';
      case 'denied': return '#fc8181';
      default: return '#a0aec0';
    }
  };

  // Get permission icon
  const permIcon = (perm) => {
    switch (perm) {
      case 'readwrite': return faLockOpen;
      case 'read': return faLock;
      case 'denied': return faExclamationTriangle;
      default: return faLock;
    }
  };

  // Get share type icon
  const shareTypeIcon = (type) => {
    if (type?.includes('hidden')) return faEyeSlash;
    switch (type?.split(' ')[0]) {
      case 'disk': return faHdd;
      case 'printer': return faPrint;
      case 'ipc': return faPlug;
      default: return faHdd;
    }
  };

  // Get file type from name
  const fileType = (name, isDir) => {
    if (isDir) return 'Folder';
    const ext = name.split('.').pop()?.toLowerCase();
    const types = {
      pdf: 'PDF', doc: 'Word', docx: 'Word', xls: 'Excel', xlsx: 'Excel',
      ppt: 'PowerPoint', pptx: 'PowerPoint', txt: 'Text', csv: 'CSV',
      jpg: 'Image', jpeg: 'Image', png: 'Image', gif: 'Image', bmp: 'Image',
      zip: 'Archive', rar: 'Archive', '7z': 'Archive', tar: 'Archive', gz: 'Archive',
      exe: 'Executable', dll: 'Library', msi: 'Installer', bat: 'Script',
      ps1: 'PowerShell', py: 'Python', js: 'JavaScript', html: 'HTML',
      xml: 'XML', json: 'JSON', log: 'Log', conf: 'Config', ini: 'Config',
    };
    return types[ext] || ext?.toUpperCase() || 'File';
  };

  // Sort entries
  const sortEntries = (entries) => {
    if (!entries) return [];
    return [...entries].sort((a, b) => {
      // Directories always first
      if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;

      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
          break;
        case 'size':
          cmp = (a.size || 0) - (b.size || 0);
          break;
        case 'modified':
          cmp = (a.modified || '').localeCompare(b.modified || '');
          break;
        case 'type':
          cmp = fileType(a.name, a.isDir).localeCompare(fileType(b.name, b.isDir));
          break;
        default:
          cmp = 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  // Toggle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Sort icon
  const sortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDir === 'asc' ? faSortUp : faSortDown;
  };

  // Sensitive filename patterns (mirrors agent-side)
  const sensitivePatterns = [
    /password/i, /credential/i, /secret/i, /\.key$/i, /\.pem$/i, /\.pfx$/i,
    /\.p12$/i, /\.cer$/i, /id_rsa/i, /\.env$/i, /web\.config$/i,
    /appsettings\.json$/i, /\.kdbx$/i, /\.rdp$/i, /\.vnc$/i,
  ];
  const isSensitiveFile = (name) => sensitivePatterns.some(p => p.test(name));

  // Get current entries for the file list pane
  const currentEntries = selectedPath ? sortEntries(browsedContents[selectedPath]) : [];

  // === TREE VIEW COMPONENT ===
  const TreeNode = ({ label, icon, iconColor, path, depth, hasChildren, isShare, shareInfo }) => {
    const isExpanded = expandedNodes.has(path);
    const isSelected = selectedPath === path;
    const isLoading = loadingPaths.has(path);
    const children = browsedContents[path]?.filter(e => e.isDir) || [];

    return (
      <div>
        <div
          onClick={() => {
            if (hasChildren || isShare) toggleNode(path);
            selectPath(path, buildBreadcrumb(path));
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '3px 8px',
            paddingLeft: `${12 + depth * 16}px`,
            cursor: 'pointer',
            background: isSelected ? 'rgba(66, 153, 225, 0.2)' : 'transparent',
            borderLeft: isSelected ? '2px solid #4299e1' : '2px solid transparent',
            fontSize: '13px',
            color: isSelected ? '#e2e8f0' : '#cbd5e0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          className="tree-node-hover"
        >
          {/* Expand/collapse arrow */}
          <span style={{ width: '16px', display: 'inline-flex', justifyContent: 'center', marginRight: '4px', flexShrink: 0 }}>
            {isLoading ? (
              <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
            ) : (hasChildren || isShare) ? (
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                style={{ fontSize: '9px', color: '#718096' }}
              />
            ) : null}
          </span>

          {/* Icon */}
          <FontAwesomeIcon
            icon={icon}
            style={{ fontSize: '12px', color: iconColor || '#a0aec0', marginRight: '6px', flexShrink: 0 }}
          />

          {/* Label */}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>

          {/* Permission badge for shares */}
          {shareInfo && (
            <span style={{
              marginLeft: '6px',
              fontSize: '10px',
              padding: '1px 5px',
              borderRadius: '3px',
              background: `${permColor(shareInfo.permissions)}22`,
              color: permColor(shareInfo.permissions),
              flexShrink: 0,
            }}>
              {shareInfo.permissions === 'readwrite' ? 'R/W' : shareInfo.permissions === 'read' ? 'R' : '✕'}
            </span>
          )}

          {/* Risk score badge */}
          {shareInfo?.riskScore > 0 && (
            <span style={{
              marginLeft: '4px',
              fontSize: '10px',
              padding: '1px 5px',
              borderRadius: '3px',
              background: shareInfo.riskScore >= 2 ? 'rgba(252, 129, 129, 0.15)' : shareInfo.riskScore >= 1 ? 'rgba(236, 201, 75, 0.15)' : 'rgba(160, 174, 192, 0.1)',
              color: shareInfo.riskScore >= 2 ? '#fc8181' : shareInfo.riskScore >= 1 ? '#ecc94b' : '#a0aec0',
              flexShrink: 0,
              fontWeight: 600,
            }}
            title={shareInfo.riskFactors?.map(f => `${f.severity.toUpperCase()}: ${f.description}`).join('\n') || ''}
            >
              ⚡{shareInfo.riskScore.toFixed(1)}
            </span>
          )}

          {/* Sensitive files indicator */}
          {shareInfo?.sensitiveFiles?.length > 0 && (
            <span style={{
              marginLeft: '4px',
              fontSize: '10px',
              padding: '1px 5px',
              borderRadius: '3px',
              background: 'rgba(252, 129, 129, 0.15)',
              color: '#fc8181',
              flexShrink: 0,
            }}
            title={`Sensitive: ${shareInfo.sensitiveFiles.join(', ')}`}
            >
              <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '9px' }} /> {shareInfo.sensitiveFiles.length}
            </span>
          )}
        </div>

        {/* Children */}
        {isExpanded && children.map(child => {
          const childPath = `${path}\\${child.name}`;
          return (
            <TreeNode
              key={childPath}
              label={child.name}
              icon={expandedNodes.has(childPath) ? faFolderOpen : faFolder}
              iconColor="#ecc94b"
              path={childPath}
              depth={depth + 1}
              hasChildren={true}
            />
          );
        })}
      </div>
    );
  };

  // Build breadcrumb from UNC path
  const buildBreadcrumb = (pathStr) => {
    if (!pathStr) return [];

    // UNC path: \\server\share\folder
    if (pathStr.startsWith('\\\\')) {
      const parts = pathStr.replace(/^\\\\/, '').split('\\');
      const segments = [];
      let current = '';
      for (let i = 0; i < parts.length; i++) {
        current = i === 0 ? `\\\\${parts[0]}` : `${current}\\${parts[i]}`;
        segments.push({ label: parts[i], path: current });
      }
      return segments;
    }

    // Local path: C:\Users\foo or /home/user
    const sep = pathStr.includes('/') ? '/' : '\\';
    const parts = pathStr.split(sep).filter(Boolean);
    const segments = [];
    let current = '';
    for (let i = 0; i < parts.length; i++) {
      if (i === 0 && sep === '\\') {
        current = parts[0]; // "C:"
      } else if (i === 0 && sep === '/') {
        current = '/' + parts[0];
      } else {
        current = current + sep + parts[i];
      }
      segments.push({ label: parts[i], path: current });
    }
    return segments;
  };

  // === NETWORK VIEW (D3) ===
  const renderNetworkView = useCallback(() => {
    if (!svgRef.current || !scanResult?.shares?.length) return;

    d3.select(svgRef.current).selectAll('*').remove();

    const container = graphContainerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight || 500;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');

    // Zoom
    const zoom = d3.zoom()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => g.attr('transform', event.transform));
    svg.call(zoom);

    // Build nodes + links — group by host
    const nodes = [];
    const links = [];
    const shares = scanResult.shares || [];

    // Get unique hosts
    const hosts = [...new Set(shares.map(s => s.host || scanResult.target))];

    // Create host nodes
    hosts.forEach(host => {
      const hostId = `host-${host}`;
      nodes.push({
        id: hostId,
        label: host,
        type: 'host',
        color: '#4299e1',
        radius: hosts.length > 1 ? 24 : 28,
      });
    });

    // Share nodes linked to their host
    shares.forEach(share => {
      const host = share.host || scanResult.target;
      const hostId = `host-${host}`;
      const shareId = `share-${host}-${share.name}`;
      nodes.push({
        id: shareId,
        label: share.name,
        type: 'share',
        shareType: share.type,
        permissions: share.permissions,
        remark: share.remark,
        color: permColor(share.permissions),
        radius: 18,
      });
      links.push({ source: hostId, target: shareId });

      // First-level folders/files
      const entries = browsedContents[share.uncPath];
      if (entries) {
        entries.filter(e => e.isDir).slice(0, 8).forEach(entry => {
          const entryId = `entry-${host}-${share.name}-${entry.name}`;
          nodes.push({
            id: entryId,
            label: entry.name,
            type: 'folder',
            color: '#ecc94b',
            radius: 10,
          });
          links.push({ source: shareId, target: entryId });
        });

        const fileCount = entries.filter(e => !e.isDir).length;
        if (fileCount > 0) {
          const filesId = `files-${host}-${share.name}`;
          nodes.push({
            id: filesId,
            label: `${fileCount} files`,
            type: 'filegroup',
            color: '#a0aec0',
            radius: 8,
          });
          links.push({ source: shareId, target: filesId });
        }
      }
    });

    // Force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(d => {
        if (d.source.type === 'host') return 120;
        return 70;
      }))
      .force('charge', d3.forceManyBody().strength(d => d.type === 'host' ? -300 : -80))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.radius + 8));

    // Links
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#4a5568')
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6);

    // Nodes
    const node = g.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Node shapes
    node.each(function(d) {
      const el = d3.select(this);

      if (d.type === 'host') {
        // Circle with server icon
        el.append('circle')
          .attr('r', d.radius)
          .attr('fill', `${d.color}33`)
          .attr('stroke', d.color)
          .attr('stroke-width', 2.5);
        el.append('text')
          .text('🖥️')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', '18px');
      } else if (d.type === 'share') {
        // Rounded rect
        el.append('rect')
          .attr('x', -d.radius)
          .attr('y', -d.radius * 0.7)
          .attr('width', d.radius * 2)
          .attr('height', d.radius * 1.4)
          .attr('rx', 4)
          .attr('fill', `${d.color}33`)
          .attr('stroke', d.color)
          .attr('stroke-width', 2);
      } else if (d.type === 'folder') {
        el.append('circle')
          .attr('r', d.radius)
          .attr('fill', `${d.color}33`)
          .attr('stroke', d.color)
          .attr('stroke-width', 1.5);
        el.append('text')
          .text('📁')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', '10px');
      } else {
        el.append('circle')
          .attr('r', d.radius)
          .attr('fill', `${d.color}22`)
          .attr('stroke', d.color)
          .attr('stroke-width', 1);
      }
    });

    // Labels
    node.append('text')
      .text(d => d.label)
      .attr('dy', d => d.type === 'host' ? d.radius + 14 : d.type === 'share' ? d.radius * 0.7 + 14 : d.radius + 12)
      .attr('text-anchor', 'middle')
      .attr('fill', '#e2e8f0')
      .attr('font-size', d => d.type === 'host' ? '12px' : '10px')
      .attr('font-weight', d => d.type === 'host' ? 'bold' : 'normal');

    // Permission labels on shares
    node.filter(d => d.type === 'share')
      .append('text')
      .text(d => d.permissions === 'readwrite' ? 'R/W' : d.permissions === 'read' ? 'R' : '✕')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', d => d.color)
      .attr('font-size', '11px')
      .attr('font-weight', 'bold');

    // Tooltip on hover
    node.append('title')
      .text(d => {
        if (d.type === 'share') return `${d.label}\nType: ${d.shareType}\nAccess: ${d.permissions}${d.remark ? '\n' + d.remark : ''}`;
        return d.label;
      });

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });
  }, [scanResult, browsedContents]);

  useEffect(() => {
    if (viewMode === 'network') {
      renderNetworkView();
    }
  }, [viewMode, renderNetworkView]);

  // Auto-fill target from beacon IP if not set
  useEffect(() => {
    if (selectedBeacon && !target) {
      const beacon = beacons.find(b => b.beaconId === selectedBeacon);
      if (beacon?.systemInfo?.ipAddresses?.length > 0) {
        // Don't auto-fill with the beacon's own IP — that's the source, not the target
      }
    }
  }, [selectedBeacon]);

  const approvedBeacons = beacons
    .filter(b => b.isApproved)
    .sort((a, b) => (b.isAlive ? 1 : 0) - (a.isAlive ? 1 : 0));

  // === EXPORT FUNCTIONS ===

  // Build hierarchical data structure for export (shares + browsed contents)
  const buildExportTree = () => {
    const data = scanResult || {};
    const shares = data.shares || [];
    const hosts = [...new Set(shares.map(s => s.host || data.target))];

    return hosts.map(host => ({
      host,
      shares: shares.filter(s => (s.host || data.target) === host).map(share => ({
        name: share.name,
        type: share.type,
        permissions: share.permissions,
        remark: share.remark,
        uncPath: share.uncPath,
        riskScore: share.riskScore || 0,
        riskFactors: share.riskFactors || [],
        sensitiveFiles: share.sensitiveFiles || [],
        accessedWith: share.accessedWith || '',
        contents: buildFileTree(share.uncPath),
      })),
    }));
  };

  // Recursively build file tree from browsedContents
  const buildFileTree = (path) => {
    const entries = browsedContents[path];
    if (!entries) return null;
    return entries.map(e => ({
      ...e,
      children: e.isDir ? buildFileTree(`${path}\\${e.name}`) : undefined,
    }));
  };

  // Export as HTML
  const exportHTML = () => {
    const tree = buildExportTree();
    const target = scanResult?.target || 'unknown';
    const timestamp = new Date().toLocaleString('sv-SE');

    const renderEntries = (entries, indent) => {
      if (!entries) return '';
      return entries.map(e => {
        const icon = e.isDir ? '📁' : '📄';
        const size = e.isDir ? '' : ` (${formatSize(e.size)})`;
        const mod = e.modified ? ` — ${formatDate(e.modified)}` : '';
        let html = `${'  '.repeat(indent)}<li>${icon} <span class="${e.isDir ? 'folder' : 'file'}">${e.name}</span>${size}${mod}`;
        if (e.children && e.children.length > 0) {
          html += `\n${'  '.repeat(indent + 1)}<ul>\n${renderEntries(e.children, indent + 2)}${'  '.repeat(indent + 1)}</ul>`;
        }
        html += `</li>\n`;
        return html;
      }).join('');
    };

    let body = '';
    tree.forEach(hostData => {
      body += `<h2>🖥️ ${hostData.host}</h2>\n`;
      hostData.shares.forEach(share => {
        const permBadge = share.permissions === 'readwrite' ? '🟢 R/W' : share.permissions === 'read' ? '🟡 R' : '🔴 Denied';
        const riskBadge = share.riskScore > 0 ? ` <span class="risk risk-${share.riskScore >= 2 ? 'high' : share.riskScore >= 1 ? 'medium' : 'low'}">Risk: ${share.riskScore.toFixed(1)}</span>` : '';
        body += `<h3>📂 ${share.name} <span class="perm">[${permBadge}]</span>${riskBadge}</h3>\n`;
        if (share.remark) body += `<p class="remark">${share.remark}</p>\n`;
        body += `<p class="path">${share.uncPath}</p>\n`;
        if (share.accessedWith) body += `<p class="remark">Accessed with: ${share.accessedWith}</p>\n`;
        // Risk factors
        if (share.riskFactors?.length > 0) {
          body += `<div class="risk-factors">\n`;
          share.riskFactors.forEach(f => {
            body += `  <div class="risk-factor risk-${f.severity}"><strong>${f.severity.toUpperCase()}</strong> ${f.description} (+${f.score.toFixed(1)})</div>\n`;
          });
          body += `</div>\n`;
        }
        // Sensitive files
        if (share.sensitiveFiles?.length > 0) {
          body += `<div class="sensitive">🔑 Sensitive files: ${share.sensitiveFiles.map(f => `<code>${f}</code>`).join(', ')}</div>\n`;
        }
        if (share.contents) {
          body += `<ul>\n${renderEntries(share.contents, 1)}</ul>\n`;
        } else {
          body += `<p class="empty">Contents not loaded</p>\n`;
        }
      });
    });

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Share Scan Report — ${target}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #1a1a2e; color: #e0e0e0; margin: 2rem; line-height: 1.6; }
  h1 { color: #4299e1; border-bottom: 2px solid #4299e1; padding-bottom: 8px; }
  h2 { color: #63b3ed; margin-top: 2rem; }
  h3 { color: #a0aec0; margin-left: 1rem; }
  .perm { font-size: 0.85em; }
  .path { color: #718096; font-family: monospace; font-size: 0.85em; margin-left: 1rem; }
  .remark { color: #a0aec0; font-style: italic; margin-left: 1rem; font-size: 0.9em; }
  .empty { color: #4a5568; font-style: italic; margin-left: 1rem; }
  ul { list-style: none; padding-left: 1.5rem; }
  li { padding: 2px 0; }
  .folder { color: #ecc94b; font-weight: 500; }
  .file { color: #cbd5e0; }
  .meta { color: #718096; font-size: 0.85em; margin-top: 2rem; border-top: 1px solid #2d3748; padding-top: 8px; }
  .risk { font-size: 0.8em; padding: 2px 8px; border-radius: 4px; margin-left: 8px; font-weight: 600; }
  .risk-high { background: rgba(252, 129, 129, 0.2); color: #fc8181; }
  .risk-medium { background: rgba(236, 201, 75, 0.2); color: #ecc94b; }
  .risk-low { background: rgba(160, 174, 192, 0.15); color: #a0aec0; }
  .risk-factors { margin: 4px 0 8px 1rem; font-size: 0.85em; }
  .risk-factor { padding: 2px 0; }
  .risk-factor.risk-high { color: #fc8181; }
  .risk-factor.risk-medium { color: #ecc94b; }
  .risk-factor.risk-low { color: #a0aec0; }
  .sensitive { margin: 4px 0 8px 1rem; font-size: 0.85em; color: #fc8181; }
  .sensitive code { background: rgba(252, 129, 129, 0.1); padding: 1px 4px; border-radius: 3px; }
</style></head><body>
<h1>Share Scan Report</h1>
<p><strong>Target:</strong> ${target} — <strong>Scanned:</strong> ${timestamp}</p>
<p><strong>Shares found:</strong> ${scanResult?.shares?.length || 0}${scanResult?.scanned > 1 ? ` across ${scanResult.withShares} of ${scanResult.scanned} hosts` : ''}</p>
${body}
<p class="meta">Generated by PurpleHQ — ${timestamp}</p>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shares-${target.replace(/[/\\:,]/g, '_')}-${new Date().toISOString().slice(0, 10)}.html`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('HTML report exported');
  };

  // Export as CSV
  const exportCSV = () => {
    const rows = [['Host', 'Share', 'Type', 'Permissions', 'UNC Path', 'Remark', 'Risk Score', 'Sensitive Files', 'Accessed With', 'File Path', 'Is Directory', 'Size (bytes)', 'Modified']];
    const shares = scanResult?.shares || [];

    shares.forEach(share => {
      const host = share.host || scanResult?.target || '';
      const entries = browsedContents[share.uncPath];
      if (entries && entries.length > 0) {
        const addEntries = (entryList, parentPath) => {
          entryList.forEach(e => {
            const fullPath = `${parentPath}\\${e.name}`;
            rows.push([host, share.name, share.type, share.permissions, share.uncPath, share.remark || '', share.riskScore || '', (share.sensitiveFiles || []).join('; '), share.accessedWith || '', fullPath, e.isDir ? 'Yes' : 'No', e.isDir ? '' : String(e.size || 0), e.modified || '']);
            // Include sub-entries if browsed
            const subEntries = browsedContents[fullPath];
            if (subEntries) {
              addEntries(subEntries, fullPath);
            }
          });
        };
        addEntries(entries, share.uncPath);
      } else {
        rows.push([host, share.name, share.type, share.permissions, share.uncPath, share.remark || '', share.riskScore || '', (share.sensitiveFiles || []).join('; '), share.accessedWith || '', '', '', '', '']);
      }
    });

    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shares-${(scanResult?.target || 'scan').replace(/[/\\:,]/g, '_')}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('CSV exported');
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        backgroundColor: '#374151',
        borderBottom: '1px solid #4b5563',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FontAwesomeIcon icon={browseMode === 'shares' ? faNetworkWired : faFolder} style={{ color: '#4299e1' }} />
          <strong style={{ color: '#fff', fontSize: '14px' }}>
            {browseMode === 'shares' ? 'Share Browser' : 'File Browser'}
          </strong>
          {browseMode === 'shares' && scanResult?.shares && (
            <Badge bg="info">{scanResult.shares.length} shares</Badge>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Browse mode toggle */}
          <div style={{ display: 'flex', gap: 0 }}>
            <Button
              size="sm"
              variant={browseMode === 'shares' ? 'info' : 'outline-secondary'}
              onClick={() => setBrowseMode('shares')}
              style={{ fontSize: '11px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            >
              <FontAwesomeIcon icon={faNetworkWired} /> Shares
            </Button>
            <Button
              size="sm"
              variant={browseMode === 'local' ? 'info' : 'outline-secondary'}
              onClick={() => setBrowseMode('local')}
              style={{ fontSize: '11px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            >
              <FontAwesomeIcon icon={faFolder} /> Local FS
            </Button>
          </div>

          {/* Beacon selector */}
          <Form.Select
            size="sm"
            value={selectedBeacon}
            onChange={e => setSelectedBeacon(e.target.value)}
            style={{ width: '220px', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563', fontSize: '12px' }}
          >
            <option value="">Select beacon...</option>
            {approvedBeacons.map(b => (
              <option key={b.beaconId} value={b.beaconId} style={{ color: b.isAlive ? '#e5e7eb' : '#6b7280' }}>
                {b.isAlive ? '● ' : '○ '}{b.systemInfo?.hostname || b.beaconId} {b.systemInfo?.ipAddresses?.[0] ? `(${b.systemInfo.ipAddresses[0]})` : ''}{!b.isAlive ? ' [offline]' : ''}
              </option>
            ))}
          </Form.Select>

          {/* Target/Path input */}
          {browseMode === 'shares' ? (
            <div style={{ display: 'flex', gap: 0 }}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Host, CIDR, or range (192.168.1.0/24)"
                value={target}
                onChange={e => setTarget(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleScan()}
                list="share-history"
                style={{ width: '180px', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563', fontSize: '12px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              <datalist id="share-history">
                {scanHistory.map(h => <option key={h} value={h} />)}
              </datalist>
              <Button
                size="sm"
                variant="primary"
                onClick={handleScan}
                disabled={scanning || !selectedBeacon || !target}
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, fontSize: '12px' }}
              >
                {scanning ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSearch} />}
                {' '}Scan
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 0 }}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Path (e.g. C:\)"
                value={localPath}
                onChange={e => setLocalPath(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLocalBrowse()}
                style={{ width: '200px', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563', fontSize: '12px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              <Button
                size="sm"
                variant="primary"
                onClick={handleLocalBrowse}
                disabled={scanning || !selectedBeacon || !localPath}
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, fontSize: '12px' }}
              >
                {scanning ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSearch} />}
                {' '}Browse
              </Button>
            </div>
          )}

          {/* Credentials toggle */}
          {browseMode === 'shares' && (
            <OverlayTrigger placement="bottom" overlay={<Tooltip>{showCreds ? 'Using custom credentials' : 'Using agent session (click to add creds)'}</Tooltip>}>
              <Button
                size="sm"
                variant={showCreds && credUsername ? 'warning' : 'outline-secondary'}
                onClick={() => setShowCreds(!showCreds)}
                style={{ fontSize: '11px' }}
              >
                <FontAwesomeIcon icon={faKey} />
              </Button>
            </OverlayTrigger>
          )}

          {/* View toggle (shares mode only) */}
          {browseMode === 'shares' && (
            <div style={{ display: 'flex', gap: 0 }}>
              <Button
                size="sm"
                variant={viewMode === 'explorer' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('explorer')}
                style={{ fontSize: '11px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              >
                <FontAwesomeIcon icon={faList} /> Explorer
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'network' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('network')}
                style={{ fontSize: '11px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                <FontAwesomeIcon icon={faProjectDiagram} /> Network
              </Button>
            </div>
          )}

          {/* Refresh + Export */}
          {(scanResult || localRoots) && (
            <>
              <Button size="sm" variant="outline-secondary" onClick={browseMode === 'shares' ? handleScan : handleLocalBrowse} disabled={scanning} title="Refresh">
                <FontAwesomeIcon icon={faSync} spin={scanning} />
              </Button>
              {scanResult?.shares?.length > 0 && (
                <>
                  <Button size="sm" variant="outline-info" onClick={exportHTML} title="Export HTML report">
                    <FontAwesomeIcon icon={faFileExport} /> HTML
                  </Button>
                  <Button size="sm" variant="outline-info" onClick={exportCSV} title="Export CSV">
                    <FontAwesomeIcon icon={faFileCsv} /> CSV
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Credentials panel (collapsible) */}
      {showCreds && browseMode === 'shares' && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: '#1f2937',
          borderBottom: '1px solid #4b5563',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
        }}>
          <FontAwesomeIcon icon={faKey} style={{ color: '#ecc94b', fontSize: '11px' }} />
          <span style={{ color: '#a0aec0', whiteSpace: 'nowrap' }}>Creds:</span>
          <Form.Control
            size="sm"
            type="text"
            placeholder="DOMAIN"
            value={credDomain}
            onChange={e => setCredDomain(e.target.value)}
            style={{ width: '100px', backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #4b5563', fontSize: '11px' }}
          />
          <span style={{ color: '#4b5563' }}>\</span>
          <Form.Control
            size="sm"
            type="text"
            placeholder="username"
            value={credUsername}
            onChange={e => setCredUsername(e.target.value)}
            style={{ width: '130px', backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #4b5563', fontSize: '11px' }}
          />
          <Form.Control
            size="sm"
            type="password"
            placeholder="password"
            value={credPassword}
            onChange={e => setCredPassword(e.target.value)}
            style={{ width: '130px', backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #4b5563', fontSize: '11px' }}
          />
          <span style={{ color: '#718096', fontSize: '11px', whiteSpace: 'nowrap' }}>
            {credUsername ? `→ go-smb2 NTLM auth` : 'Empty = agent session'}
          </span>
          {credUsername && (
            <Button
              size="sm"
              variant="link"
              onClick={() => { setCredUsername(''); setCredPassword(''); setCredDomain(''); }}
              style={{ padding: '0 4px', color: '#718096', fontSize: '11px' }}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {/* Content */}
      {!(browseMode === 'shares' ? scanResult : localRoots) && !scanning ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#718096' }}>
          <FontAwesomeIcon icon={browseMode === 'shares' ? faNetworkWired : faFolder} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
          <p style={{ fontSize: '14px', margin: 0 }}>
            {browseMode === 'shares'
              ? 'Select a beacon and enter a target to enumerate network shares'
              : 'Select a beacon and enter a path to browse the remote file system'}
          </p>
          <p style={{ fontSize: '12px', marginTop: '8px', color: '#4a5568' }}>
            {browseMode === 'shares'
              ? 'Pure Go SMB2 scanner — no syscalls, no subprocesses, Defender-safe'
              : 'Browse files and folders on the agent\'s machine — uses browse_path command'}
          </p>
        </div>
      ) : scanning && !(browseMode === 'shares' ? scanResult : localRoots) ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#a0aec0' }}>
          {/* Scanning progress indicator */}
          <div style={{ maxWidth: '420px', margin: '0 auto' }}>
            {/* Spinner + status */}
            <div style={{ marginBottom: '16px' }}>
              {scanStatus === 'agent_offline' ? (
                <FontAwesomeIcon icon={faSkullCrossbones} style={{ fontSize: '36px', color: '#fc8181', marginBottom: '8px' }} />
              ) : (
                <Spinner animation="border" style={{ width: '36px', height: '36px', marginBottom: '8px' }} />
              )}
              <p style={{ fontSize: '14px', margin: 0, fontWeight: 500 }}>
                {browseMode === 'shares' ? `Scanning shares on ${target}` : `Browsing ${localPath}`}
              </p>
            </div>

            {/* Progress bar (animated pulse for indeterminate, estimated for ranges) */}
            {browseMode === 'shares' && (
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#2d3748',
                borderRadius: '2px',
                overflow: 'hidden',
                marginBottom: '12px',
              }}>
                <div style={{
                  height: '100%',
                  borderRadius: '2px',
                  background: scanStatus === 'agent_offline'
                    ? '#fc8181'
                    : 'linear-gradient(90deg, #4299e1, #63b3ed, #4299e1)',
                  backgroundSize: '200% 100%',
                  animation: 'scanPulse 1.5s ease-in-out infinite',
                  width: '100%',
                }} />
              </div>
            )}

            {/* Status details */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              fontSize: '12px',
              color: '#718096',
            }}>
              {/* Elapsed time */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FontAwesomeIcon icon={faClock} style={{ fontSize: '11px' }} />
                <span>Elapsed: <strong style={{ color: '#a0aec0' }}>{formatElapsed(scanElapsed)}</strong></span>
              </div>

              {/* Status line */}
              <div>
                {scanStatus === 'creating' && 'Creating task...'}
                {scanStatus === 'waiting' && 'Task queued — waiting for agent check-in...'}
                {scanStatus === 'pending' && 'Task pending — waiting for agent to pick up...'}
                {scanStatus === 'sent' && (
                  browseMode === 'shares'
                    ? (estimateHostCount(target) > 1
                        ? `Agent scanning ~${estimateHostCount(target)} hosts (SMB port 445 → share enumeration)`
                        : 'Agent enumerating shares and pre-fetching first level...')
                    : 'Agent listing directory contents...'
                )}
                {scanStatus === 'agent_offline' && (
                  <span style={{ color: '#fc8181' }}>
                    ⚠ Agent appears offline — may have crashed or been killed by Defender
                  </span>
                )}
                {!scanStatus && 'Initializing...'}
              </div>

              {/* Range estimate */}
              {browseMode === 'shares' && estimateHostCount(target) > 1 && (
                <div style={{ color: '#4a5568', fontSize: '11px' }}>
                  Range scan: ~{estimateHostCount(target)} hosts • Estimated ~{Math.ceil(estimateHostCount(target) * 0.5)}s–{Math.ceil(estimateHostCount(target) * 2)}s
                </div>
              )}
            </div>

            {/* Stop button */}
            <Button
              size="sm"
              variant={scanStatus === 'agent_offline' ? 'danger' : 'outline-danger'}
              onClick={handleStop}
              style={{ marginTop: '20px', fontSize: '12px', padding: '6px 20px' }}
            >
              <FontAwesomeIcon icon={faStop} style={{ marginRight: '6px' }} />
              {scanStatus === 'agent_offline' ? 'Abort (Agent Offline)' : 'Stop Scan'}
            </Button>
          </div>
        </div>
      ) : browseMode === 'local' || viewMode === 'explorer' ? (
        /* === EXPLORER VIEW === */
        <div style={{ display: 'flex', height: '550px' }}>
          {/* Tree pane (left) */}
          <div style={{
            width: '280px',
            minWidth: '200px',
            borderRight: '1px solid #4b5563',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: '#1a202c',
            paddingTop: '4px',
          }}>
            {browseMode === 'shares' ? (
              <>
                {/* Summary header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#e2e8f0',
                  borderBottom: '1px solid #2d3748',
                }}>
                  <span>
                    <FontAwesomeIcon icon={faServer} style={{ color: '#4299e1', marginRight: '8px' }} />
                    {scanResult?.target}
                  </span>
                  {scanResult?.scanned > 1 && (
                    <span style={{ fontSize: '10px', color: '#718096', fontWeight: 'normal' }}>
                      {scanResult.withShares}/{scanResult.scanned} hosts
                    </span>
                  )}
                </div>

                {/* Group shares by host */}
                {(() => {
                  const shares = scanResult?.shares || [];
                  // Get unique hosts (preserve order)
                  const hosts = [...new Set(shares.map(s => s.host || scanResult?.target))];
                  const isMultiHost = hosts.length > 1;

                  if (shares.length === 0) {
                    return (
                      <div style={{ padding: '12px', color: '#718096', fontSize: '12px', textAlign: 'center' }}>
                        No shares found
                      </div>
                    );
                  }

                  return hosts.map(host => {
                    const hostShares = shares.filter(s => (s.host || scanResult?.target) === host);
                    const hostKey = `host-${host}`;
                    const isHostExpanded = !isMultiHost || expandedNodes.has(hostKey);

                    return (
                      <div key={host}>
                        {/* Host header (only in multi-host mode) */}
                        {isMultiHost && (
                          <div
                            onClick={() => toggleNode(hostKey)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#a0aec0',
                              background: 'rgba(66, 153, 225, 0.05)',
                              borderBottom: '1px solid #2d3748',
                            }}
                            className="tree-node-hover"
                          >
                            <span style={{ width: '16px', display: 'inline-flex', justifyContent: 'center', marginRight: '4px' }}>
                              <FontAwesomeIcon
                                icon={isHostExpanded ? faChevronDown : faChevronRight}
                                style={{ fontSize: '9px', color: '#718096' }}
                              />
                            </span>
                            <FontAwesomeIcon icon={faServer} style={{ fontSize: '11px', color: '#4299e1', marginRight: '6px' }} />
                            {host}
                            <Badge bg="secondary" style={{ marginLeft: '6px', fontSize: '9px' }}>
                              {hostShares.length}
                            </Badge>
                          </div>
                        )}

                        {/* Shares for this host */}
                        {isHostExpanded && hostShares.map(share => (
                          <TreeNode
                            key={share.uncPath}
                            label={share.name}
                            icon={shareTypeIcon(share.type)}
                            iconColor={permColor(share.permissions)}
                            path={share.uncPath}
                            depth={isMultiHost ? 1 : 0}
                            hasChildren={share.type?.startsWith('disk') && share.permissions !== 'denied'}
                            isShare={true}
                            shareInfo={share}
                          />
                        ))}
                      </div>
                    );
                  });
                })()}
              </>
            ) : (
              <>
                {/* Local FS root */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#e2e8f0',
                  borderBottom: '1px solid #2d3748',
                }}>
                  <FontAwesomeIcon icon={faHdd} style={{ color: '#48bb78', marginRight: '8px' }} />
                  {localPath.trim().replace(/\\$/, '')}
                </div>

                {/* Local folder tree */}
                {localRoots?.filter(e => e.isDir).map(entry => {
                  const entryPath = `${localPath.trim().replace(/\\$/, '')}\\${entry.name}`;
                  return (
                    <TreeNode
                      key={entry.name}
                      label={entry.name}
                      icon={expandedNodes.has(entryPath) ? faFolderOpen : faFolder}
                      iconColor="#ecc94b"
                      path={entryPath}
                      depth={0}
                      hasChildren={true}
                    />
                  );
                })}

                {localRoots && localRoots.filter(e => e.isDir).length === 0 && (
                  <div style={{ padding: '12px', color: '#718096', fontSize: '12px', textAlign: 'center' }}>
                    No subdirectories
                  </div>
                )}
              </>
            )}
          </div>

          {/* File list pane (right) */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Breadcrumb */}
            {breadcrumb.length > 0 && (
              <div style={{
                padding: '6px 12px',
                borderBottom: '1px solid #4b5563',
                backgroundColor: '#1f2937',
                fontSize: '12px',
                color: '#a0aec0',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
              }}>
                {breadcrumb.map((seg, i) => (
                  <React.Fragment key={seg.path}>
                    {i > 0 && <span style={{ color: '#4b5563' }}>›</span>}
                    <span
                      onClick={() => selectPath(seg.path, breadcrumb.slice(0, i + 1))}
                      style={{
                        cursor: 'pointer',
                        color: i === breadcrumb.length - 1 ? '#e2e8f0' : '#a0aec0',
                        fontWeight: i === breadcrumb.length - 1 ? '500' : '400',
                      }}
                      className="breadcrumb-hover"
                    >
                      {seg.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* File list */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
              {!selectedPath ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096', fontSize: '13px' }}>
                  <FontAwesomeIcon icon={faFolder} style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }} />
                  <p>Select a share or folder from the tree</p>
                </div>
              ) : loadingPaths.has(selectedPath) ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0' }}>
                  <Spinner animation="border" size="sm" className="mb-2" />
                  <p style={{ fontSize: '12px' }}>Loading contents...</p>
                </div>
              ) : !browsedContents[selectedPath] ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096', fontSize: '13px' }}>
                  Loading...
                </div>
              ) : (
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #4b5563', position: 'sticky', top: 0 }}>
                      <th
                        onClick={() => handleSort('name')}
                        style={{ padding: '6px 12px', textAlign: 'left', color: '#a0aec0', cursor: 'pointer', fontWeight: '500', userSelect: 'none' }}
                      >
                        Name <FontAwesomeIcon icon={sortIcon('name')} style={{ fontSize: '10px', marginLeft: '4px' }} />
                      </th>
                      <th
                        onClick={() => handleSort('size')}
                        style={{ padding: '6px 12px', textAlign: 'right', color: '#a0aec0', cursor: 'pointer', fontWeight: '500', width: '100px', userSelect: 'none' }}
                      >
                        Size <FontAwesomeIcon icon={sortIcon('size')} style={{ fontSize: '10px', marginLeft: '4px' }} />
                      </th>
                      <th
                        onClick={() => handleSort('type')}
                        style={{ padding: '6px 12px', textAlign: 'left', color: '#a0aec0', cursor: 'pointer', fontWeight: '500', width: '100px', userSelect: 'none' }}
                      >
                        Type <FontAwesomeIcon icon={sortIcon('type')} style={{ fontSize: '10px', marginLeft: '4px' }} />
                      </th>
                      <th
                        onClick={() => handleSort('modified')}
                        style={{ padding: '6px 12px', textAlign: 'left', color: '#a0aec0', cursor: 'pointer', fontWeight: '500', width: '160px', userSelect: 'none' }}
                      >
                        Modified <FontAwesomeIcon icon={sortIcon('modified')} style={{ fontSize: '10px', marginLeft: '4px' }} />
                      </th>
                      <th style={{ padding: '6px 12px', width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEntries.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#718096' }}>
                          Empty directory
                        </td>
                      </tr>
                    ) : currentEntries.map(entry => {
                      const entryPath = `${selectedPath}\\${entry.name}`;
                      const isDownloading = downloadingFiles.has(entryPath);

                      return (
                        <tr
                          key={entry.name}
                          style={{
                            borderBottom: '1px solid #2d3748',
                            cursor: entry.isDir ? 'pointer' : 'default',
                          }}
                          className="file-row-hover"
                          onDoubleClick={() => {
                            if (entry.isDir) {
                              const newPath = entryPath;
                              toggleNode(newPath);
                              selectPath(newPath, [...breadcrumb, { label: entry.name, path: newPath }]);
                            }
                          }}
                        >
                          <td style={{ padding: '5px 12px', color: !entry.isDir && isSensitiveFile(entry.name) ? '#fc8181' : '#e2e8f0' }}>
                            <FontAwesomeIcon
                              icon={entry.isDir ? faFolder : faFile}
                              style={{
                                color: entry.isDir ? '#ecc94b' : !entry.isDir && isSensitiveFile(entry.name) ? '#fc8181' : '#a0aec0',
                                marginRight: '8px',
                                fontSize: '12px',
                              }}
                            />
                            {entry.name}
                            {!entry.isDir && isSensitiveFile(entry.name) && (
                              <span style={{
                                marginLeft: '6px',
                                fontSize: '9px',
                                padding: '1px 4px',
                                borderRadius: '3px',
                                background: 'rgba(252, 129, 129, 0.15)',
                                color: '#fc8181',
                                fontWeight: 600,
                              }}>
                                SENSITIVE
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', color: '#a0aec0' }}>
                            {entry.isDir ? '—' : formatSize(entry.size)}
                          </td>
                          <td style={{ padding: '5px 12px', color: '#718096' }}>
                            {fileType(entry.name, entry.isDir)}
                          </td>
                          <td style={{ padding: '5px 12px', color: '#718096' }}>
                            {formatDate(entry.modified)}
                          </td>
                          <td style={{ padding: '5px 12px', textAlign: 'center' }}>
                            {!entry.isDir && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Download file</Tooltip>}>
                                <Button
                                  size="sm"
                                  variant="link"
                                  style={{ padding: '0 4px', color: isDownloading ? '#718096' : '#4299e1' }}
                                  onClick={() => handleDownload(entryPath, entry.name)}
                                  disabled={isDownloading}
                                >
                                  {isDownloading ? (
                                    <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                                  ) : (
                                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: '12px' }} />
                                  )}
                                </Button>
                              </OverlayTrigger>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Status bar */}
            {selectedPath && browsedContents[selectedPath] && (
              <div style={{
                padding: '4px 12px',
                borderTop: '1px solid #4b5563',
                backgroundColor: '#1f2937',
                fontSize: '11px',
                color: '#718096',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span>
                  {browsedContents[selectedPath].filter(e => e.isDir).length} folders,{' '}
                  {browsedContents[selectedPath].filter(e => !e.isDir).length} files
                </span>
                <span>
                  Total: {formatSize(browsedContents[selectedPath].filter(e => !e.isDir).reduce((s, e) => s + (e.size || 0), 0))}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* === NETWORK VIEW === */
        <div ref={graphContainerRef} style={{ height: '550px', backgroundColor: '#1a202c' }}>
          <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
        </div>
      )}

      {/* CSS for hover effects + scan animation */}
      <style>{`
        .tree-node-hover:hover { background: rgba(74, 85, 104, 0.3) !important; }
        .file-row-hover:hover { background: rgba(66, 153, 225, 0.08) !important; }
        .breadcrumb-hover:hover { color: #63b3ed !important; text-decoration: underline; }
        @keyframes scanPulse {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </Card>
  );
};

export default ShareBrowser;
